/**
 * Conversation thread (1:1 or group): E2E messages, text sending with an option
 * for ephemeral message, delivery status and verification by safety number.
 */

import { createEffect, createSignal, For, onCleanup, Show } from "solid-js";
import { createRoute, useNavigate, useParams } from "@tanstack/solid-router";
import {
  ArrowLeft,
  Check,
  CheckCheck,
  Clock,
  Lock,
  Mic,
  Paperclip,
  Phone,
  SendHorizonal,
  ShieldCheck,
  Square,
  Timer,
  Users,
  Video,
} from "@fleet/icons";
import { msg } from "@lingui/core/macro";

import { rootRoute } from "@/routes/__root";
import { useChat } from "@/providers/chat-provider";
import { useI18n } from "@/providers/i18n-provider";
import { Button } from "@/components/ui/button";
import { Atmosphere } from "@/components/ui/atmosphere";
import { AttachmentView } from "@/components/chat/attachment-view";
import { useBackDismiss } from "@/lib/use-back-dismiss";
import { canSendAttachmentOf, entitlementsFor, getPlan } from "@/lib/monetization/entitlements";
import {
  mediaKindFromMime,
  probeImageSize,
  probeVideoMeta,
  readFileBytes,
  VoiceRecorder,
} from "@/lib/media-capture";
import type { MessageStatus } from "@/lib/chat/types";

const DEFAULT_EXPIRE_SECS = 30;

/** TTL of an ephemeral message; override by `window.__EXPIRE_SECS__` (e2e). */
function expireSecs(): number {
  const override: unknown = Reflect.get(globalThis, "__EXPIRE_SECS__");
  return typeof override === "number" && override > 0 ? override : DEFAULT_EXPIRE_SECS;
}

function StatusIcon(props: { status: MessageStatus }) {
  return (
    <Show
      when={props.status === "delivered"}
      fallback={
        <Show when={props.status === "sent"} fallback={<Clock class="h-3 w-3 opacity-70" />}>
          <Check class="h-3 w-3 opacity-70" />
        </Show>
      }
    >
      <CheckCheck class="h-3 w-3 text-success" />
    </Show>
  );
}

function ConversationComponent() {
  const chat = useChat();
  const { t } = useI18n();
  const navigate = useNavigate();
  const params = useParams({ from: conversationRoute.id });

  const [draft, setDraft] = createSignal("");
  const [ephemeral, setEphemeral] = createSignal(false);
  const [safety, setSafety] = createSignal<string | undefined>();
  const [showSafety, setShowSafety] = createSignal(false);
  const [recording, setRecording] = createSignal(false);
  const [busy, setBusy] = createSignal(false);
  // Monetization framework gate (flags, NO ads): attachments that are too heavy.
  const [attachLimitHit, setAttachLimitHit] = createSignal(false);
  let fileInput: HTMLInputElement | undefined;
  const recorder = new VoiceRecorder();

  const conversationId = () => params().conversationId;
  const conversation = () => chat.getConversation(conversationId());
  const messages = () => chat.messagesFor(conversationId());
  const membersLabel = (count: number) => t(msg`${count} members`);
  const isDirect = () => conversation()?.kind === "direct";
  const expireOrUndefined = () => (ephemeral() ? expireSecs() : undefined);

  // P2P-first transport: on opening a 1:1 thread we attempt (best-effort, with
  // throttle in the manager) to open the direct DataChannel with the peer. The badge
  // reflects whether the envelopes travel directly (p2p) or through the blind mailbox (relay).
  const p2pActive = () => {
    const peer = conversation()?.peerDeviceId;
    return peer ? chat.p2pConnected(peer) : false;
  };
  createEffect(() => {
    const conv = conversation();
    if (chat.ready() && conv?.kind === "direct" && conv.peerDeviceId) {
      chat.ensureP2p(conv.peerDeviceId);
    }
  });

  const sendFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) {
      return;
    }
    setBusy(true);
    setAttachLimitHit(false);
    try {
      for (const file of [...files]) {
        // Plan capability gate (product, not security): heavy media.
        if (!canSendAttachmentOf(file.size, getPlan())) {
          setAttachLimitHit(true);
          continue;
        }
        const bytes = await readFileBytes(file);
        const mime = file.type || "application/octet-stream";
        const mediaKind = mediaKindFromMime(mime);
        let width: number | undefined;
        let height: number | undefined;
        let durationMs: number | undefined;
        if (mediaKind === "image" || mediaKind === "video") {
          const probeUrl = URL.createObjectURL(file);
          try {
            if (mediaKind === "image") {
              const meta = await probeImageSize(probeUrl);
              ({ width } = meta);
              ({ height } = meta);
            } else {
              const meta = await probeVideoMeta(probeUrl);
              ({ width } = meta);
              ({ height } = meta);
              ({ durationMs } = meta);
            }
          } finally {
            URL.revokeObjectURL(probeUrl);
          }
        }
        await chat.sendAttachment(
          conversationId(),
          { name: file.name || "archivo", mime, mediaKind, bytes, width, height, durationMs },
          expireOrUndefined(),
        );
      }
    } finally {
      setBusy(false);
      if (fileInput) {
        fileInput.value = "";
      }
    }
  };

  const toggleRecording = async () => {
    if (recording()) {
      setRecording(false);
      setBusy(true);
      try {
        const { blob, mime, durationMs } = await recorder.stop();
        const bytes = await readFileBytes(blob);
        await chat.sendAttachment(
          conversationId(),
          { name: `nota-de-voz.webm`, mime, mediaKind: "voice", bytes, durationMs },
          expireOrUndefined(),
        );
      } finally {
        setBusy(false);
      }
      return;
    }
    try {
      await recorder.start();
      setRecording(true);
    } catch (err) {
      console.warn("[chat] microphone unavailable", err);
    }
  };

  onCleanup(() => {
    recorder.cancel();
  });

  let scroller: HTMLDivElement | undefined;
  createEffect(() => {
    messages().length;
    if (scroller) {
      scroller.scrollTop = scroller.scrollHeight;
    }
  });

  // Plain local for lingui to extract the message (settings-fonts pattern).
  const attachLimitNotice = () => {
    const maxMib = Math.floor(entitlementsFor(getPlan()).maxAttachmentBytes / (1024 * 1024));
    return t(
      msg`That file exceeds your plan's attachment limit (${maxMib} MiB). Plus unlocks heavy media — no ads, ever.`,
    );
  };

  const send = async () => {
    const text = draft().trim();
    if (!text) {
      return;
    }
    setDraft("");
    await chat.sendText(conversationId(), text, ephemeral() ? expireSecs() : undefined);
  };

  const toggleSafety = async () => {
    if (!showSafety()) {
      setSafety(await chat.safetyNumber(conversationId()));
    }
    setShowSafety(!showSafety());
  };

  // The "back" button closes the safety-number panel (overlay) instead of
  // leaving the thread, premium PWA/mobile pattern.
  useBackDismiss(showSafety, () => setShowSafety(false));

  return (
    <div class="relative flex h-dvh w-full flex-col">
      <Atmosphere />
      <div class="mx-auto flex h-dvh w-full max-w-2xl flex-col">
        <header class="flex items-center gap-2.5 border-b border-[var(--separator)] bg-[var(--glass)] p-3 backdrop-blur-xl">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => void navigate({ to: "/" })}
            aria-label={t(msg`Back`)}
          >
            <ArrowLeft class="h-5 w-5" />
          </Button>
          <div
            class="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-[var(--accent-foreground)]"
            style={{
              background: "linear-gradient(135deg, oklch(0.72 0.13 278), oklch(0.66 0.12 210))",
            }}
          >
            <Show when={conversation()?.kind === "group"} fallback={<Lock class="h-4 w-4" />}>
              <Users class="h-5 w-5" />
            </Show>
          </div>
          <div class="min-w-0 flex-1">
            <div class="truncate font-semibold" data-testid="conversation-title">
              {conversation()?.title ?? conversationId()}
            </div>
            <div class="flex items-center gap-1.5 text-xs text-muted-foreground">
              <Lock class="h-3 w-3 shrink-0 text-success" />
              <span class="truncate">
                <Show
                  when={conversation()?.kind === "group"}
                  fallback={conversation()?.peerDeviceId}
                >
                  {membersLabel(conversation()?.memberDeviceIds.length ?? 0)}
                </Show>
              </span>
              <Show when={isDirect()}>
                <span
                  data-testid="transport-badge"
                  data-transport={p2pActive() ? "p2p" : "relay"}
                  class={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold tracking-wide uppercase ${
                    p2pActive()
                      ? "bg-success/15 text-success"
                      : "bg-[var(--surface-secondary)] text-muted-foreground"
                  }`}
                >
                  <Show when={p2pActive()} fallback={t(msg`Relay`)}>
                    {t(msg`P2P direct`)}
                  </Show>
                </span>
              </Show>
            </div>
          </div>
          <Show when={isDirect() && conversation()?.peerDeviceId}>
            {(peerId) => (
              <>
                <Button
                  variant="ghost"
                  size="icon"
                  data-testid="call-voice-button"
                  aria-label={t(msg`Voice call`)}
                  onClick={() => void chat.startCall(peerId(), "audio")}
                >
                  <Phone class="h-5 w-5" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  data-testid="call-video-button"
                  aria-label={t(msg`Video call`)}
                  onClick={() => void chat.startCall(peerId(), "video")}
                >
                  <Video class="h-5 w-5" />
                </Button>
              </>
            )}
          </Show>
          <Show when={conversation()?.kind === "direct"}>
            <Button
              variant={showSafety() ? "default" : "ghost"}
              size="icon"
              data-testid="safety-number-button"
              aria-label={t(msg`Safety number`)}
              onClick={() => void toggleSafety()}
            >
              <ShieldCheck class="h-5 w-5" />
            </Button>
          </Show>
        </header>

        <Show when={showSafety()}>
          <div
            class="animate-scale-in border-b border-[var(--separator)] bg-[var(--surface-secondary)]/70 p-5 text-center backdrop-blur"
            data-testid="safety-number-panel"
          >
            <div class="mb-2 inline-flex items-center gap-2 rounded-full bg-success/12 px-3 py-1 text-xs font-medium text-success">
              <ShieldCheck class="h-3.5 w-3.5" />
              {t(msg`Safety number`)}
            </div>
            <p class="mx-auto mb-3 max-w-sm text-xs text-muted-foreground">
              {t(msg`Compare this number with your contact to verify the connection.`)}
            </p>
            <code
              data-testid="safety-number"
              class="mx-auto block max-w-md rounded-xl border border-success/25 bg-[var(--field-background)] px-4 py-3 font-mono text-sm tracking-[0.18em] break-all text-foreground"
            >
              <Show when={safety()} fallback={t(msg`Not available yet`)}>
                {safety()}
              </Show>
            </code>
          </div>
        </Show>

        <div
          ref={scroller}
          class="flex-1 space-y-2.5 overflow-x-hidden overflow-y-auto px-4 py-5"
          data-testid="message-list"
        >
          <Show
            when={messages().length > 0}
            fallback={
              <div class="flex flex-col items-center gap-3 pt-16 text-center">
                <div class="flex h-14 w-14 items-center justify-center rounded-2xl bg-success/12 text-success">
                  <Lock class="h-6 w-6" />
                </div>
                <p class="max-w-xs text-sm text-muted-foreground">
                  {t(msg`No messages yet. Say hello — end-to-end encrypted.`)}
                </p>
              </div>
            }
          >
            <For each={messages()}>
              {(m) => (
                <div
                  data-testid="chat-message"
                  data-direction={m.direction}
                  class={`flex flex-col ${m.direction === "out" ? "items-end" : "items-start"}`}
                >
                  <div
                    class={`max-w-[82%] px-3.5 py-2 text-sm shadow-sm ${
                      m.direction === "out"
                        ? "rounded-[1.15rem] rounded-br-md bg-primary text-primary-foreground"
                        : "rounded-[1.15rem] rounded-bl-md border border-[var(--glass-border)] bg-[var(--surface-secondary)] text-surface-secondary-foreground"
                    }`}
                  >
                    <Show when={conversation()?.kind === "group" && m.direction === "in"}>
                      <div class="mb-0.5 font-mono text-[11px] font-medium text-success/90">
                        {m.authorDevice}
                      </div>
                    </Show>
                    <Show when={m.attachment}>
                      {(att) => (
                        <AttachmentView attachment={att()} outgoing={m.direction === "out"} />
                      )}
                    </Show>
                    <Show when={m.text}>
                      <span class="whitespace-pre-wrap break-words">{m.text}</span>
                    </Show>
                    <div class="mt-0.5 flex items-center justify-end gap-1 text-[11px] opacity-70">
                      <Show when={m.expiresInSecs !== undefined}>
                        <Timer class="h-3 w-3" />
                      </Show>
                      <Show when={m.direction === "out"}>
                        <span
                          data-testid="msg-status"
                          data-status={m.status}
                          class="inline-flex items-center"
                        >
                          <StatusIcon status={m.status} />
                        </span>
                      </Show>
                    </div>
                  </div>
                </div>
              )}
            </For>
          </Show>
        </div>

        <div class="border-t border-[var(--separator)] bg-[var(--glass)] p-3 backdrop-blur-xl">
          <Show when={attachLimitHit()}>
            <p
              role="status"
              data-testid="attachment-limit-notice"
              class="mb-2 rounded-xl border border-amber-500/40 bg-amber-500/10 px-4 py-2 text-xs text-foreground"
            >
              {attachLimitNotice()}
            </p>
          </Show>
          <div class="flex items-center gap-2">
            <Button
              variant={ephemeral() ? "default" : "ghost"}
              size="icon"
              class="shrink-0 rounded-full"
              data-testid="ephemeral-toggle"
              aria-pressed={ephemeral()}
              aria-label={t(msg`Disappearing message`)}
              onClick={() => setEphemeral(!ephemeral())}
            >
              <Timer class="h-5 w-5" />
            </Button>
            {/* Adjuntos y notas de voz E2E: disponibles en 1:1 Y en grupos
                (en grupo viajan cifrados por la sender-key del emisor). */}
            <input
              ref={fileInput}
              data-testid="attach-input"
              type="file"
              class="hidden"
              onChange={(e) => void sendFiles(e.currentTarget.files)}
            />
            <Button
              variant="ghost"
              size="icon"
              class="shrink-0 rounded-full"
              data-testid="attach-button"
              disabled={busy() || recording()}
              aria-label={t(msg`Attach a file`)}
              onClick={() => fileInput?.click()}
            >
              <Paperclip class="h-5 w-5" />
            </Button>
            <Button
              variant={recording() ? "default" : "ghost"}
              size="icon"
              class="shrink-0 rounded-full"
              data-testid="voice-record-button"
              aria-pressed={recording()}
              disabled={busy() && !recording()}
              aria-label={
                recording() ? t(msg`Stop and send voice note`) : t(msg`Record a voice note`)
              }
              onClick={() => void toggleRecording()}
            >
              <Show when={recording()} fallback={<Mic class="h-5 w-5" />}>
                <Square class="h-4 w-4 fill-current" />
              </Show>
            </Button>
            <input
              data-testid="message-input"
              class="min-w-0 flex-1 rounded-full border border-[var(--field-border)] bg-[var(--field-background)] px-4 py-2.5 text-sm text-foreground placeholder:text-[var(--field-placeholder)] outline-none transition-shadow focus:ring-2 focus:ring-ring/60"
              placeholder={ephemeral() ? t(msg`Disappearing message…`) : t(msg`Message`)}
              value={draft()}
              onInput={(e) => setDraft(e.currentTarget.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  void send();
                }
              }}
            />
            <Button
              size="icon"
              class="shrink-0 rounded-full"
              data-testid="send-button"
              aria-label={t(msg`Send`)}
              onClick={() => void send()}
            >
              <SendHorizonal class="h-5 w-5" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export const conversationRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/c/$conversationId",
  component: ConversationComponent,
});
